<template>
  <AnimatedContainer :stagger="0.075">
    <Form
      key="form"
      ref="form"
      v-slot="{ errors }"
      novalidate
      class="grid max-w-216 grid-cols-4 gap-5 sm:grid-cols-12 sm:gap-6"
      @submit="onSubmit"
    >
      <template
        v-for="inputField in formBlueprint.fields"
        :key="inputField.handle"
      >
        <!-- FORM TITLES AND DESCRIPTIONS -->
        <FormTitle
          v-if="inputField.handle.startsWith('form_title_')"
          :field="inputField"
          class="slide-left"
        />

        <!-- TEXT -->
        <InputField
          v-else-if="
            inputField.type === 'text'
              || (inputField.type === 'textarea'
                && inputField.handle !== 'mail_text')
          "
          :field="inputField"
          :rules="formBlueprint.rules[inputField.handle]"
          :error="errors[inputField.handle]"
          class="slide-left"
        />

        <!-- SELECT -->
        <FormSelect
          v-else-if="inputField.type === 'select'"
          :form-field="inputField"
          :rules="formBlueprint.rules[inputField.handle]"
          :error="errors[inputField.handle]"
          class="slide-left"
        />

        <!-- CHECKBOX GROUP -->
        <!-- <CheckboxGroup
          v-else-if="
            inputField.type === 'checkboxes'
              && inputField.handle !== 'accept_conditions'
          "
          :field="inputField"
          :rules="formBlueprint.rules[inputField.handle]"
          :error="errors[inputField.handle]"
          :options="inputField.config.options"
          class="slide-left"
        /> -->

        <!-- RADIO GROUP -->
        <!-- <RadioGroup
          v-else-if="inputField.type === 'radio'"
          :field="inputField"
          :rules="formBlueprint.rules[inputField.handle]"
          :error="errors[inputField.handle]"
          :options="inputField.config.options"
          class="slide-left"
        /> -->

        <!-- FILE UPLOAD -->
        <InputFile
          v-else-if="inputField.type === 'assets'"
          :field="inputField"
          :rules="formBlueprint.rules[inputField.handle]"
          :error="errors[inputField.handle]"
          :instructions="inputField.instructions"
          multiple
          class="slide-left"
        />
      </template>

      <!-- CHECKBOX ACCEPT CONDITIONS -->
      <CheckboxGroup
        v-if="textCheckboxConditions"
        key="accept_conditions"
        :field="{
          handle: 'accept_conditions',
          width: 100,
        }"
        class="slide-left"
        label="Bestimmungen"
        rules="required"
        :error="errors.accept_conditions"
        :options="{
          accept_conditions: textCheckboxConditions,
        }"
      />

      <!-- HONEYPOT -->
      <Field :name="formBlueprint.honeypot">
        <input class="hidden" :name="formBlueprint.honeypot" />
      </Field>

      <div class="slide-left col-span-12 mt-3 flex items-center justify-self-start sm:mt-6">
        <AppButton
          :disabled="state === 'success'"
          type="submit"
          :is-loading="state === 'pending'"
        >
          {{ textSubmitButton || globalsForms.text_submit_button }}
        </AppButton>
      </div>

      <!-- ALERT BANNER -->
      <div ref="collapseRef" class="col-span-12">
        <p
          v-if="state === 'success'"
          class="w-max max-w-text text-success"
          v-html="textSuccess || globalsForms.text_success"
        />
        <div
          v-else-if="state === 'error'"
          class="w-max max-w-text text-error"
          v-html="textError || globalsForms.text_error "
        />
      </div>

      <slot :errors="errors" />
    </Form>
  </AnimatedContainer>
</template>

<!-- eslint-disable-next-line vue/block-lang -->
<script setup>
import { Form, Field } from 'vee-validate'

const props = defineProps({
  formBlueprint: {
    type: Object,
    default: () => ({}),
  },
  textCheckboxConditions: {
    type: String,
    default: '',
  },
  textSubmitButton: {
    type: String,
    default: 'Absenden',
  },
  textSuccess: {
    type: String,
    default: 'Das Formular wurde erfolgreich abgeschickt',
  },
  textError: {
    type: String,
    default: 'Ups, etwas ist schief gelaufen. Bitte versuche es später erneut.',
  },
  parentHandlesSubmit: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['submit'])

const config = useRuntimeConfig()
const state = ref('idle')
const form = ref(null)

const globals = inject(GlobalsKey, null)
const globalsForms = computed(() => globals?.value?.forms)

const collapseRef = ref(null)
const options = {
  duration: 300,
  initiallyClosed: true,
}

const openBanner = useCollapse(collapseRef, options)

function initFormData(fields) {
  // forget undefined fields
  fields = Object.keys(fields).reduce((acc, key) => {
    if (fields[key] !== undefined) {
      acc[key] = fields[key]
    }
    return acc
  }, {})

  // convert fields to array
  fields = Object.keys(fields).map(key => ({
    handle: key,
    value: fields[key],
  }))

  // If a filed of type assets is empty statamic requires a empty array (instead of undefined)
  // Therefore we need to check if a field of type assets is undefined and set it to []
  const undefinedFileFields = fields.filter((field) => {
    return (
      field.value === undefined
      && props.formBlueprint.fields.some(
        blueprintField =>
          blueprintField.type === 'assets'
          && blueprintField.handle === field.handle,
      )
    )
  })

  // Set value of undefinedFileFields to empty array []
  undefinedFileFields.forEach((field) => {
    field.value = []
  })

  const formData = new FormData()

  fields.forEach((field) => {
    // check if value is an array of File objects
    if (field.value instanceof Array && field.value[0] instanceof File) {
      field.value.forEach((file, index) => {
        // Append each file with a unique key
        formData.append(`${field.handle}[${index}]`, file)
      })
    } else if (field.value instanceof Array) {
      // This will handle non-file arrays
      field.value.forEach((item) => {
        formData.append(`${field.handle}[]`, item)
      })
    } else {
      formData.append(field.handle, field.value)
    }
  })

  return formData
}

async function onSubmit(fields) {
  if (props.parentHandlesSubmit) {
    emit('submit', fields)
    return
  }

  state.value = 'pending'

  try {
    await $fetch(
      `${config.public.CMS_URL}/!/forms/${props.formBlueprint.handle}`,
      {
        method: 'POST',
        body: initFormData(fields),
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      },
    )

    state.value = 'success'
    resetForm()
    await nextTick()
    openBanner.value = true
  } catch (error) {
    state.value = 'error'
    if (error.response) {
      // When response status is not OK
      const errors = error.response._data?.errors
      console.error('Form Submit', errors)
    } else {
      // When there is no response or other error occurs
      console.error('Error:', error.message || error)
    }

    await nextTick()
    openBanner.value = true
  }
}

function resetForm() {
  form.value.resetForm()
}

defineExpose({
  resetForm,
})
</script>
